/**
 * Keeps trying something that can fail on a poor connection, a few times with growing gaps,
 * instead of trying once and giving up silently.
 */

/** Waits 3 s, 6 s, 12 s, 24 s, 48 s between tries — six attempts in about 90 s, then it stops. */
export const DEFAULT_RETRY_DELAYS_MS = [3_000, 6_000, 12_000, 24_000, 48_000];

/**
 * Runs `attempt` until it reports success (true) or the delays run out.
 * Resolves true if it ever succeeded. `sleep` is injectable for tests.
 */
export async function retryWithBackoff(
  attempt: () => Promise<boolean>,
  delaysMs: readonly number[] = DEFAULT_RETRY_DELAYS_MS,
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
): Promise<boolean> {
  for (let i = 0; i <= delaysMs.length; i++) {
    let ok = false;
    try {
      ok = await attempt();
    } catch {
      ok = false;
    }
    if (ok) return true;
    const delay = delaysMs[i];
    if (delay === undefined) break;
    await sleep(delay);
  }
  return false;
}
